import '@/styles/globals.css';

import Bugsnag from '@/utils/bugsnag';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useState, useEffect } from 'react';

import { initAmplitude, WEB_APP_AMPLITUDE_CONFIG } from '@/utils/amplitude';

import { Alert, Navigation } from '@/components';
import ReloadPrompt from '@/components/ReloadPrompt';
import { AppContextWrapper } from '@/context';
import { ProvideAuth, useEffectOnce } from '@/hooks';
import useIsIframe from '@/hooks/useIsIframe';
import styles from '@/styles/_app.module.css';
import '@/i18n/config';
import I18nInit from '@/i18n/init-client';

// Custom localStorage persister for React Query v4
const createLocalStoragePersister = () => ({
  persistClient: async (client: TypeAny) => {
    if (typeof window !== 'undefined') {
      try {
        const cacheData = {
          timestamp: Date.now(),
          buster: 'beatly-v1',
          clientState: client
        };
        localStorage.setItem('beatly-react-query-cache', JSON.stringify(cacheData));
      } catch (error) {
        // Silently handle localStorage errors
      }
    }
  },
  restoreClient: async () => {
    if (typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem('beatly-react-query-cache');
        if (cached) {
          const { timestamp, buster, clientState } = JSON.parse(cached);
          const maxAge = 10 * 60 * 1000; // 10 minutes

          // Check if cache is still valid
          if (Date.now() - timestamp < maxAge && buster === 'beatly-v1') {
            return clientState;
          }
          localStorage.removeItem('beatly-react-query-cache');
        }
      } catch (error) {
        localStorage.removeItem('beatly-react-query-cache');
      }
    }
    return undefined;
  },
  removeClient: async () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('beatly-react-query-cache');
    }
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      // NOTE: what we really want is refetchOnMount: 'never', but that's not supported in TanStack v4
      // If we update to a newer version of TanStack, we can change this to refetchOnMount: 'never'
      refetchOnWindowFocus: false,
      refetchOnMount: false
    }
  }
});

// Persist the query client cache to localStorage
if (typeof window !== 'undefined') {
  const persister = createLocalStoragePersister();
  persistQueryClient({
    queryClient,
    persister,
    maxAge: 10 * 60 * 1000, // Persist for 10 minutes
    buster: 'beatly-v1' // Cache buster for app updates
  });
}

function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const isIframe = useIsIframe();

  // Initialize with false to avoid hydration mismatch, will be updated in useEffect
  const [showNavbar, setShowNavbar] = useState(false);

  // Reload prompt state for ChunkLoadError
  const [showReloadPrompt, setShowReloadPrompt] = useState(false);

  useEffectOnce(() => {
    // Initialize Amplitude (environment-aware, disabled for non-production)
    initAmplitude(WEB_APP_AMPLITUDE_CONFIG);

    // Add ChunkLoadError handler for missing assets
    const handleChunkLoadError = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const errorMessage = String(error);

      // Check if it's a chunk loading error
      if (
        errorMessage.includes('ChunkLoadError') ||
        errorMessage.includes('Loading chunk') ||
        errorMessage.includes('Loading CSS chunk') ||
        errorMessage.includes('Failed to fetch dynamically imported module')
      ) {
        console.warn(
          '🔄 ChunkLoadError detected - likely due to new deployment. Reloading page...',
          error
        );

        // Show reload prompt instead of confirm dialog
        setShowReloadPrompt(true);
      }
    };

    // Add global error handler for chunk loading errors
    window.addEventListener('unhandledrejection', handleChunkLoadError);

    // Cleanup function
    return () => {
      window.removeEventListener('unhandledrejection', handleChunkLoadError);
    };
  });

  // Single method to calculate navbar visibility based on current route
  const shouldShowNavbar = () => {
    const currentPath = router.asPath || '/';
    // Remove language prefix pattern: /<two-letters>/ (e.g., /en/, /sv/, /de/)
    const baseRoute = currentPath.replace(/^\/[a-z]{2}\//, '') || '/';
    return !baseRoute.startsWith('/login') && !baseRoute.startsWith('/signup');
  };

  // Create error boundary only if Bugsnag is available
  const ErrorBoundary = Bugsnag?.getPlugin('react')?.createErrorBoundary(React) || React.Fragment;

  // Update navbar visibility based on current route
  useEffect(() => {
    const updateNavbarVisibility = () => {
      const shouldShow = shouldShowNavbar();

      console.log('🔍 Navbar Debug:', {
        asPath: router.asPath,
        pathname: router.pathname,
        shouldShowNavbar: shouldShow
      });

      setShowNavbar(shouldShow);
    };

    // Update immediately
    updateNavbarVisibility();

    // Listen for route changes
    router.events.on('routeChangeComplete', updateNavbarVisibility);

    return () => {
      router.events.off('routeChangeComplete', updateNavbarVisibility);
    };
  }, [router.asPath, router.events]);

  // Reload prompt handlers
  const handleReload = () => {
    window.location.reload();
  };

  const handleDismissReloadPrompt = () => {
    setShowReloadPrompt(false);
  };

  return (
    <ErrorBoundary>
      <Head>
        <title>Beatly</title>
        <meta name="viewport" content="width=device-width, initial-scale=1 maximum-scale=1" />
        <link href="/favicon.ico" rel="shortcut icon" />
      </Head>
      <I18nInit />
      <QueryClientProvider client={queryClient}>
        <AppContextWrapper>
          <Alert />
          <ProvideAuth>
            <div className={`${styles.app} ${isIframe ? styles.noSpacing : ''}`}>
              {showNavbar && <Navigation />}
              <div
                className={`${styles.appComponent} ${!isIframe && showNavbar ? styles.spacing : ''}`}
              >
                <Component {...pageProps} />
              </div>
            </div>
          </ProvideAuth>
        </AppContextWrapper>
      </QueryClientProvider>

      {/* Reload prompt for version updates */}
      <ReloadPrompt
        isVisible={showReloadPrompt}
        onReload={handleReload}
        onDismiss={handleDismissReloadPrompt}
        autoReloadDelay={10000} // 10 seconds
      />
    </ErrorBoundary>
  );
}

export default App;
